import { useMutation, useQuery, useReactiveVar, useApolloClient } from '@apollo/client';
import React, { useState, useMemo } from 'react';
import { Page } from '../../../layout';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import EditPageHeader from './EditPageHeader';
import CustomSelector from '../../../shared/components/CustomSelector';
import _ from 'lodash';
import ApolloError from '../../../shared/components/ApolloError';
import Asterisk from '../../../shared/components/Asterisk';
import { Button, Checkbox, Collapse } from '@mui/material';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import GET_USER_LICENSE_BY_USER_ID from '../../queries/getUserLicenseByUserIdQuery';
import EDIT_USER_LICENSE from '../../queries/editUserLicenseMutation';
import { isUserAdminTier } from '../../../shared/services/checkUserTierService';
import CheckedXIcon from '../../../shared/components/CheckedXIcon';
import { userPrivileges, userTier } from '../../../../apollo/cache';
import SkeletonWrapper from '../../../shared/components/skeleton/SkeletonWrapper';
import PermissionLists from '../shared/components/PermissionLists/PermissionLists';
import { Breadcrumb } from '../../../shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '../../../shared/services/pageTitleService';
import { useUserRoleOptions } from '../shared/hooks/useUserRoleOptions';
import { getUserAssignEditPermissions } from '../shared/services/userAssignEditPermissionsService';
import { PAGE_MODE } from '../../constants/pageModeTypes';
import AppraiserInfo from '../assignLicense/AppraiserInfo/AppraiserInfo';
import { DEFAULT_APPRAISER_DATA } from '../../../AppraiserForm/constants/AppraiserInfoConstants';
import AppraiserDataMapper from '../../../AppraiserForm/mappers/AppraiserDataMapper';
import AppraiserInputValidator from '../../../AppraiserForm/validators/AppraiserInputValidator';
import EDIT_USER_LICENSE_TO_APPRAISER from '../../queries/editUserLicenseToAppraiserMutation';
import { APPRAISER_SYSTEM_ROLES } from '../shared/constants/appraiserSystemRoles';
import SUPPLEMENTAL_ROLE from '../../../../constants/supplemental-Roles';
import { useAppraiserTabs } from '../shared/hooks/useAppraiserTabs';
import ResponsiveUrlTabMenu from '@shared/components/ResponsiveUrlTabMenu';
import AtmCheckbox from '../shared/components/AtmCheckbox';
import REMOVE_LICENSE from '../../queries/removeLicenseMutation';
import LicenseDataMapper from '../../mappers/LicenseDataMapper';
import ViewInvoicesCheckbox from '../shared/components/ViewInvoicesCheckbox';
import ChangeLicense from './changeLicense/ChangeLicense';

const CUSTOM_PERMISSION_KEYS = {
  VIEW_INVOICES: 'canAccessInvoices',
  SUPER_USER: 'superUser',
  IS_ATM: 'isAtm'
};

const DEFAULT_CUSTOM_PERMISSIONS = {
  [CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]: false,
  [CUSTOM_PERMISSION_KEYS.SUPER_USER]: false,
  [CUSTOM_PERMISSION_KEYS.IS_ATM]: false
};

const DEFAULT_PERMISSIONS = {
  ohts: [],
  orgIds: [],
  orgUnitIds: []
};

/**
 * @typedef {object} EditLicenseUserData
 * @property {string} email
 * @property {string} firstName
 * @property {string} lastName
 * @property {number} [externalUserId]
 * @property {number|null} [roleId]
 * @property {number|null} [supplementalRoleId]
 * @property {boolean} [superUser]
 * @property {boolean} [canAccessInvoices]
 * @property {boolean} [isAppraiser]
 * @property {boolean} [isDeactivated]
 * @property {number[]} ohtIds
 * @property {number[]} orgIds
 * @property {number[]} orgUnitIds
 */

const EditLicensePage = () => {
  const location = useLocation();
  const client = useApolloClient();

  const { userId, pageMode, orgId, orgUnitId, orgName, isFree, isDeactivated } =
    location.state || {};

  const currentUserTier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);

  const defaultAppraiserFormData = _.cloneDeep(DEFAULT_APPRAISER_DATA);

  const [userDataBeforeEdit, setUserDataBeforeEdit] = useState(
    /** @type {EditLicenseUserData} */ ({})
  );
  const [userData, setUserData] = useState(
    /** @type {EditLicenseUserData} */ ({
      email: '',
      firstName: '',
      lastName: ''
    })
  );
  const [isEdit, setIsEdit] = useState(false);
  const [permissionData, setPermissionData] = useState(DEFAULT_PERMISSIONS);
  const [selectedRole, setSelectedRole] = useState();
  const [customPermissionData, setCustomPermissionData] = useState(DEFAULT_CUSTOM_PERMISSIONS);
  const [appraiserData, setAppraiserData] = useState(defaultAppraiserFormData);

  const {
    isUserAbleToSetSuperUserCheckbox,
    isUserAbleToSetATMCheckbox,
    isUserAbleToDeactivateUser,
    isUserAbleToSetViewInvoicesCheckbox,
    shouldShowPermissions
  } = getUserAssignEditPermissions(currentUserTier, pageMode, privileges, isFree, selectedRole);

  const isAppraiserLicense =
    !userDataBeforeEdit.isAppraiser &&
    ((customPermissionData[CUSTOM_PERMISSION_KEYS.IS_ATM] && isUserAbleToSetATMCheckbox) ||
      APPRAISER_SYSTEM_ROLES.includes(selectedRole));

  const previousLink = isUserAdminTier(currentUserTier)
    ? `/user-management/${orgId}`
    : '/user-management';

  const pageTitle = getPageTitle([orgName, 'Edit License']);
  const annotationItems = orgName ? [orgName] : [];
  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: previousLink
    },
    { name: 'Edit License', url: '' }
  ];

  const navigate = useNavigate();

  const disableSubmitButton = useMemo(() => {
    if (_.isEmpty(userDataBeforeEdit)) {
      return true;
    }

    if (
      isAppraiserLicense &&
      Boolean(AppraiserInputValidator.validateAssignLicenseToAppraiserData(appraiserData).error)
    ) {
      return true;
    }

    const result =
      _.isEqual(userDataBeforeEdit.roleId, selectedRole) &&
      _.isEqual(
        userDataBeforeEdit.superUser,
        customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]
      ) &&
      _.isEqual(
        userDataBeforeEdit.canAccessInvoices,
        customPermissionData[CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]
      ) &&
      _.isEqual(userDataBeforeEdit.ohtIds.sort(), permissionData.ohts.sort()) &&
      _.isEqual(userDataBeforeEdit.orgIds.sort(), permissionData.orgIds.sort()) &&
      _.isEqual(userDataBeforeEdit.orgUnitIds.sort(), permissionData.orgUnitIds.sort()) &&
      !isAppraiserLicense;
    return result;
  }, [
    userDataBeforeEdit,
    selectedRole,
    permissionData,
    customPermissionData,
    isAppraiserLicense,
    appraiserData
  ]);

  const {
    appraiserTabs,
    loading: appraiserTabsLoading,
    error: appraiserTabsError
  } = useAppraiserTabs(userId);

  const { loading: userLicenseLoading, error: userLicenseError } = useQuery(
    GET_USER_LICENSE_BY_USER_ID,
    {
      variables: { externalUserId: userId },
      fetchPolicy: 'no-cache',
      onCompleted: response => {
        const userData = LicenseDataMapper.toDataPage(response?.userLicense);
        setUserDataBeforeEdit({ ...userData });
        resetEditFormData(userData);
      }
    }
  );

  const { userRoleOptions, userRoleOptionsLoading, userRoleOptionsError } = useUserRoleOptions(
    orgId,
    orgUnitId,
    isFree
  );

  const [editLicense, { error: editLicenseError }] = useMutation(EDIT_USER_LICENSE, {
    update: () => client.clearStore()
  });

  const [editUserLicenseToAppraiser, { error: editUserLicenseToAppraiserError }] = useMutation(
    EDIT_USER_LICENSE_TO_APPRAISER,
    { update: () => client.clearStore() }
  );

  const [removeLicense, { error: removeLicenseError }] = useMutation(REMOVE_LICENSE, {
    update: () => client.clearStore()
  });

  const handleSuperUserCheckboxClick = value => {
    updateCustomPermission(CUSTOM_PERMISSION_KEYS.SUPER_USER, value);
    if (value) {
      setPermissionData(DEFAULT_PERMISSIONS);
    }
  };

  const handleRoleChange = value => {
    setSelectedRole(value);

    if (
      !customPermissionData[CUSTOM_PERMISSION_KEYS.IS_ATM] &&
      !APPRAISER_SYSTEM_ROLES.includes(value)
    ) {
      setAppraiserData(defaultAppraiserFormData);
    }
  };

  const handleATMCheckboxChange = value => {
    updateCustomPermission(CUSTOM_PERMISSION_KEYS.IS_ATM, value);

    if (!value) {
      setAppraiserData(defaultAppraiserFormData);
    }
  };

  const handleEditClick = () => {
    setIsEdit(true);
  };

  const handleCancelClick = () => {
    const userData = { ...userDataBeforeEdit };
    resetEditFormData(userData);
    setIsEdit(false);
    setAppraiserData(defaultAppraiserFormData);
  };

  const resetEditFormData = userData => {
    setUserData({ ...userData });
    setSelectedRole(userData.roleId);
    setCustomPermissionData({
      [CUSTOM_PERMISSION_KEYS.SUPER_USER]: userData.superUser,
      [CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]: userData.canAccessInvoices ?? false,
      [CUSTOM_PERMISSION_KEYS.IS_ATM]: userData.supplementalRoleId === SUPPLEMENTAL_ROLE.ATM
    });
    setPermissionData({
      ohts: userData.ohtIds,
      orgIds: userData.orgIds,
      orgUnitIds: userData.orgUnitIds
    });
  };

  const handleOnConfirmRemoveClick = async () => {
    await removeLicense({ variables: { externalUserId: userData.externalUserId } });

    navigate(previousLink, {
      state: {
        licensesModifiedSuccessfullyMessage: 'User Successfully Removed.'
      }
    });
  };

  const editUserLicense = async userInfo => {
    await editLicense({ variables: { user: userInfo } });
  };

  const editLicenseToAppraiser = async userInfo => {
    const appraiserInfo = AppraiserDataMapper.appraiserDataToApolloInput(appraiserData);

    await editUserLicenseToAppraiser({ variables: { user: userInfo, appraiser: appraiserInfo } });
  };

  const handleSubmitClick = async () => {
    const userInfo = {
      ExternalUserId: userData.externalUserId,
      SystemRoleId: selectedRole,
      SupplementalRoleId: userData.supplementalRoleId,
      AllowedOrgIds: permissionData.orgIds,
      AllowedOrgUnitIds: permissionData.orgUnitIds,
      AllowedOhtIds: permissionData.ohts,
      IsSuperUser: isUserAbleToSetSuperUserCheckbox
        ? customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]
        : null,
      CanAccessInvoices: isUserAbleToSetViewInvoicesCheckbox
        ? customPermissionData[CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]
        : null
    };

    const editLicenseMutation = isAppraiserLicense ? editLicenseToAppraiser : editUserLicense;
    await editLicenseMutation(userInfo);

    navigate(previousLink, {
      state: {
        licensesModifiedSuccessfullyMessage: 'Edits Submitted Successfully.'
      }
    });
  };

  const updateCustomPermission = (permission, value) => {
    setCustomPermissionData(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  if (!location.state) {
    const redirectionLink = isUserAdminTier(currentUserTier)
      ? '/user-management/organization-search'
      : '/user-management';

    return <Navigate to={redirectionLink} />;
  }

  if (
    userLicenseError ||
    editLicenseError ||
    userRoleOptionsError ||
    editUserLicenseToAppraiserError ||
    removeLicenseError ||
    appraiserTabsError
  ) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={breadcrumbPathItems} annotationItems={annotationItems} />

      <Collapse in={userDataBeforeEdit.isAppraiser}>
        <div className='user-management-tabs'>
          <ResponsiveUrlTabMenu
            tabs={appraiserTabs}
            urlParams={{
              orgId,
              userId
            }}
            loading={appraiserTabsLoading}
          />
        </div>
      </Collapse>

      <div className='license-page'>
        <EditPageHeader
          pageMode={pageMode}
          isAppraiserLicense={isAppraiserLicense}
          isFree={isFree}
          isDeactivated={isDeactivated}
          isUserAbleToDeactivateUser={isUserAbleToDeactivateUser}
        />
        <h4 className='field-title'>Email</h4>

        <div className='field-buttons'>
          <SkeletonWrapper show={userLicenseLoading}>
            <input
              className='input-field disabled'
              id='email'
              type='text'
              value={userData.email}
              disabled
            />
          </SkeletonWrapper>

          <div className='edit-buttons'>
            {!isEdit && (
              <Button
                data-testid='edit-button'
                className='primary-button sm'
                onClick={handleEditClick}>
                Edit
              </Button>
            )}
            {isEdit && (
              <div className='buttons'>
                <Button
                  id='cancel-button'
                  className='secondary-button sm'
                  onClick={handleCancelClick}
                  type='button'>
                  Cancel
                </Button>
                <Button
                  id='submit-button'
                  data-testid='submit-button'
                  className='primary-button sm'
                  onClick={handleSubmitClick}
                  disabled={disableSubmitButton}>
                  Submit
                </Button>
              </div>
            )}
          </div>
        </div>

        <h4 className='field-title'>
          First Name
          <Asterisk />
        </h4>
        <SkeletonWrapper show={userLicenseLoading}>
          <input
            className='input-field disabled'
            id='firstName'
            type='text'
            value={userData.firstName}
            disabled
          />
        </SkeletonWrapper>

        <h4 className='field-title'>
          Last Name
          <Asterisk />
        </h4>
        <SkeletonWrapper show={userLicenseLoading}>
          <input
            className='input-field disabled'
            id='lastName'
            type='text'
            value={userData.lastName}
            disabled
          />
        </SkeletonWrapper>
        {pageMode !== PAGE_MODE.ISACAVipOrg && (
          <CustomSelector
            id='roles-selector'
            title='User Role'
            placeholder='Select a User Role'
            testId='roles-selector'
            disabled={!isEdit || userDataBeforeEdit.isAppraiser}
            value={selectedRole}
            setValue={handleRoleChange}
            options={userRoleOptions}
            loading={userLicenseLoading || !Boolean(selectedRole) || userRoleOptionsLoading}
          />
        )}

        {isUserAbleToSetSuperUserCheckbox && (
          <div className='checkbox-wrapper'>
            <h4 className='field-title'>Super User?</h4>
            <Checkbox
              id='super-user-checkbox'
              value={customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]}
              size='small'
              checked={customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]}
              checkedIcon={<CheckedXIcon size='small' />}
              onChange={e => handleSuperUserCheckboxClick(e.target.checked)}
              color='default'
              disabled={!isEdit}
            />
          </div>
        )}

        {isUserAbleToSetViewInvoicesCheckbox && (
          <ViewInvoicesCheckbox
            checked={customPermissionData[CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]}
            onChange={e =>
              updateCustomPermission(CUSTOM_PERMISSION_KEYS.VIEW_INVOICES, e.target.checked)
            }
            disabled={!isEdit}
          />
        )}

        {isUserAbleToSetATMCheckbox && (
          <AtmCheckbox
            pageMode={pageMode}
            isAtm={customPermissionData[CUSTOM_PERMISSION_KEYS.IS_ATM]}
            setIsAtm={handleATMCheckboxChange}
            disabled={!isEdit || userDataBeforeEdit.isAppraiser}
          />
        )}

        <Collapse in={isAppraiserLicense} unmountOnExit data-testid='appraiser-form'>
          <AppraiserInfo
            appraiserData={appraiserData}
            setAppraiserData={setAppraiserData}
            appraiserSystemRoleId={selectedRole}
          />
        </Collapse>

        {shouldShowPermissions && (
          <PermissionLists
            pageMode={pageMode}
            orgId={orgId}
            permissions={permissionData}
            setPermissions={setPermissionData}
            disabled={!isEdit || customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]}
            isUserRoleSelected
          />
        )}

        {/* We need to remove this functionality when we remove feature flag DEACTIVATED_LICENSES */}
        {!isUserAbleToDeactivateUser && isEdit && !userDataBeforeEdit.isAppraiser && (
          <ConfirmDialogWithButton
            handleOnConfirm={handleOnConfirmRemoveClick}
            message='Are you sure you want to remove this licensed user?'
            openModalBtnClassName='deactivate-button'
            openModalBtnText='Remove'
            confirmBtnText='Remove'
            cancelBtnText='Cancel'
          />
        )}

        {isUserAbleToDeactivateUser && isEdit && (
          <ChangeLicense
            isUserDeactivated={userDataBeforeEdit.isDeactivated}
            isAppraiser={userDataBeforeEdit.isAppraiser}
            pageMode={pageMode}
            orgId={orgId}
            orgUnitId={orgUnitId}
            isFree={isFree}
            externalUserId={userData.externalUserId}
            previousLink={previousLink}
            userRoleId={userData.roleId}
          />
        )}
      </div>
    </Page>
  );
};

export default EditLicensePage;
